import React, { useState, useCallback } from 'react'
import { OrderDetails, OrderGoodsDetail } from '@/types'
import { BadgeType } from '@/components/Badge'
import {
  OrderCard,
  Row,
  OrderStatus,
  DetailItem,
  Divider,
  CardWrapper,
} from '../components'
import Text from '@/components/Text'
import { ImageIcon } from '@/icon'
import { primary } from '@/constant'
import { Skeleton, Stack } from '@mui/material'
import GppBadIcon from '@mui/icons-material/GppBad'
import GppGoodIcon from '@mui/icons-material/GppGood'
import Payment from './payment'
import voucher_light_icon from '@/asset/img/voucher_light_icon.webp'
import VoucherCode from './voucher'
import { PaymentStatusEnum, PayTypeEnum } from '@/types'

const formatGoodsDetails = (detail: string) => {
  const durations = { D: '日', M: '月', Y: '年' }
  let goodsDetail
  try {
    goodsDetail = JSON.parse(detail) as OrderGoodsDetail
  } catch (e) {}
  if (goodsDetail) {
    const details = [
      {
        name: goodsDetail?.package_name,
        value: `${goodsDetail?.duration || ''} ${
          durations[goodsDetail?.purchase_duration_unit] || ''
        }`,
      },
    ]
    goodsDetail?.items?.forEach((item) => details.push(item))
    return details
  } else {
    return [{ name: '', value: '' }]
  }
}

export interface OrderProps {
  data: OrderDetails;
  loading: boolean;
  onCancel: (orderCode: string) => void;
}

// 1:未支付；2:已支付；3:已退款；4:已取消 5:超时关闭
const orderStatusName = [
  '无',
  '未支付',
  '已支付',
  '已退款',
  '已取消',
  '超时关闭',
]
const orderStatusColor: BadgeType[] = [
  'info',
  'error',
  'success',
  'error',
  'disabled',
  'disabled',
]

const Order: React.FC<OrderProps> = (props) => {
  const { data, loading } = props
  const [hover, setHover] = useState<boolean>(false)
  const [code, setCode] = useState<string>()

  const disabledCard =
    data?.status === PaymentStatusEnum.canceled ||
    data?.status === PaymentStatusEnum.expaired

  const detailPage = useCallback(
    (order_code: string) => () => {
      if (order_code)
        window.open(
          `/console/organization/orders/detail?order_code=${order_code}`,
          '_blank'
        )
    },
    []
  )

  const handleShowCode = useCallback((code: string) => {
    setCode(code)
  }, [])

  const handleHideCode = useCallback(() => {
    // @ts-ignore
    setCode(null)
  }, [])

  const onClickVoucherIcon = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      handleShowCode(data?.redeem_code)
    },
    [data?.redeem_code, handleShowCode]
  )

  const changeHoverState = useCallback(
    (state: boolean) => () => {
      !disabledCard && setHover(state)
    },
    [disabledCard]
  )

  const renderPayType = useCallback(
    (payType: number) => {
      const payTypes = [
        '其他',
        '支付宝',
        '微信',
        <Text
          key={'vourcher-code'}
          size='xs'
          weight='bold'
          sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          兑换码
          <ImageIcon
            sx={{
              width: '20px',
              cursor: 'pointer',
              padding: '0px 4px',
              borderRadius: '4px',
              backgroundColor: '#ffffff80',
            }}
            onClick={onClickVoucherIcon}
            src={voucher_light_icon}
          />
        </Text>,
      ]
      return payTypes?.[payType]
    },
    [onClickVoucherIcon]
  )

  return (
    <CardWrapper
      onClick={detailPage(data?.order_code)}
      disabled={disabledCard ? 'true' : 'false'}
      onMouseEnter={changeHoverState(true)}
      onMouseLeave={changeHoverState(false)}
    >
      <OrderCard disabled={disabledCard ? 'true' : 'false'}>
        <Text
          id='order-name'
          weight='bold'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: primary,
          }}
        >
          <ImageIcon
            sx={{
              backgroundColor: loading ? '#f9f9f9' : 'transparent',
              width: '32px',
              height: '32px',
            }}
            // @ts-ignore
            src={data?.logo_url}
          />
          {loading ? <Skeleton height={32} width={100} /> : data?.app_name}
        </Text>
        {loading ? (
          <Skeleton height={14} width={120} />
        ) : (
          <Text
            id='order-created-time'
            sx={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
            weight='thin'
          >
            创建时间：{data?.created_at}
            <OrderStatus
              id='order-status'
              shape={'square'}
              type={orderStatusColor[data?.status]}
              bordered={'true'}
              backgroundstyle='gradient'
            >
              {data?.status === 2 ? <GppGoodIcon /> : <GppBadIcon />}
              <span id='status-content'>{orderStatusName?.[data?.status]}</span>
            </OrderStatus>
          </Text>
        )}
        <Row id='order-price' sx={{ alignItems: 'flex-start', gap: '8px' }}>
          {loading ? (
            <Skeleton height={44} width={44} />
          ) : (
            <>
              <Text id='order-price-symbol'>¥</Text>
              <Text id='order-price-number' size='lg'>
                {data?.total_amount}
              </Text>
            </>
          )}
        </Row>
        <Stack sx={{ gap: '8px', height: '100%' }}>
          {loading ? (
            [1, 2, 3].map((item) => (
              <Skeleton key={`order-detail-${item}`} height={20} width={100} />
            ))
          ) : (
            <>
              <DetailItem label='订单金额（元）' content={''} />
              <Divider id='order-card-divider' sx={{ margin: '24px 0px' }} />
              <DetailItem label='订单号' content={data?.order_code} />
              <DetailItem
                label='套餐内容'
                listView={true}
                content={formatGoodsDetails(data?.goods_detail)}
              />
              {data?.status === PaymentStatusEnum.paid && (
                <>
                  <DetailItem label='付款人' content={data?.creator_name} />
                  <DetailItem
                    label='支付方式'
                    content={renderPayType(data?.pay_type) ?? '其他'}
                  />
                  {data?.pay_type !== PayTypeEnum.voucher && (
                    <DetailItem label='实付金额' content={data?.real_amount} />
                  )}
                </>
              )}
              <Payment
                hover={hover}
                paid={data?.status === PaymentStatusEnum.paid}
                orderCode={data?.order_code}
                showTime={
                  data?.status === PaymentStatusEnum.unpaid ||
                  data?.status === PaymentStatusEnum.expaired
                }
                endTime={data?.expire_time_seconds}
                status={data?.status}
                payType={data?.pay_type}
              />
            </>
          )}
        </Stack>
      </OrderCard>
      {/*   @ts-ignore */}
      <VoucherCode code={code} onClose={handleHideCode} />
    </CardWrapper>
  )
}

export default Order
